import { createFileRoute } from "@tanstack/react-router";
import { buildMeta } from "@/lib/seo";
import { Fragment, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { listOpioidsPublic } from "@/lib/public-substances.functions";
import { FentanylWarnings } from "@/components/FentanylWarnings";
import { MethadonWarnings } from "@/components/MethadonWarnings";
import { PageSources } from "@/components/SourcesSheet";
import { SOURCES } from "@/data/sources";
import { errorMessage } from "@/lib/error-capture";

const opioidQuery = queryOptions({
  queryKey: ["public-opioids"],
  queryFn: () => listOpioidsPublic(),
});

export const Route = createFileRoute("/opioide")({
  head: () =>
    buildMeta({
      title: "Opioid-Effekte-Vergleich",
      description:
        "Vergleichstabelle von Opioiden: Äquivalenzdosen zu Morphin, Wirkprofile, Atemdepression und Halbwertszeiten in einer Übersicht.",
      path: "/opioide",
    }),
  loader: ({ context }) => context.queryClient.ensureQueryData(opioidQuery),
  errorComponent: ({ error }) => <div role="alert" style={{ padding: "2rem", color: "#f87171" }}>Fehler: {errorMessage(error)}</div>,
  notFoundComponent: () => <div style={{ padding: "2rem" }}>Keine Opioid-Daten gefunden.</div>,
  component: OpioidePage,
});

type Compound = {
  Substance: string;
  Class: string;
  "Equiv. to Morphin 10mg": string;
  "Typical Dose (mg)": string;
  "Max Daily (mg)": string;
  Analgesic: number;
  Sedative: number;
  "Respiratory Depression": number;
  Euphoria: number;
  "Dependence Risk": number;
  Notes: string;
  Notes_de?: string;
  pharma?: Record<string, string>;
  warning?: boolean;
  nonComparable?: boolean;
};


function DotRating({ value, max = 5, color }: { value: number; max?: number; color: string }) {
  return (
    <span style={{ letterSpacing: "0.1em", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
      {Array.from({ length: max }).map((_, i) => (
        <span key={i} style={{ color: i < value ? color : "var(--text-muted)" }}>
          {i < value ? "●" : "○"}
        </span>
      ))}
    </span>
  );
}

const TH: React.CSSProperties = {
  background: "rgba(168,85,247,0.1)",
  color: "var(--text-secondary)",
  fontSize: "0.8rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: "0.6rem 0.75rem",
  textAlign: "left",
  borderBottom: "1px solid var(--border-purple)",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const TD: React.CSSProperties = {
  padding: "0.5rem 0.75rem",
  fontSize: "0.85rem",
  color: "var(--text-primary)",
  borderBottom: "1px solid rgba(168,85,247,0.08)",
};

const PHARMA_LABELS: Record<string, string> = {
  "Half-Life": "Halbwertszeit",
  "Active Metabolites": "Aktive Metaboliten",
  "Onset of Action": "Wirkeintritt",
  "Peak Level": "Peak",
  "Duration Category": "Wirkdauer",
  "Bioavailability": "Bioverfügbarkeit",
  "Primary Metabolic Pathway": "Metabolismus",
  "Special Populations": "Besondere Hinweise",
};

const PHARMA_ORDER = [
  "Half-Life",
  "Active Metabolites",
  "Onset of Action",
  "Peak Level",
  "Duration Category",
  "Bioavailability",
  "Primary Metabolic Pathway",
  "Special Populations",
];

function OpioidePage() {
  const { data: compounds } = useSuspenseQuery(opioidQuery) as { data: Compound[] };
  const ALL = "__ALL__";
  const classes = useMemo<string[]>(
    () => [ALL, ...Array.from(new Set(compounds.map((c) => c.Class).filter((x) => !!x)))],
    [compounds],
  );
  const [cls, setCls] = useState<string>(ALL);
  const [expanded, setExpanded] = useState<string | null>(null);
  type SortKey =
    | "Class"
    | "Typical Dose (mg)"
    | "Equiv. to Morphin 10mg"
    | "Max Daily (mg)"
    | "Half-Life"
    | "Analgesic"
    | "Sedative"
    | "Respiratory Depression"
    | "Euphoria"
    | "Dependence Risk";
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" } | null>(null);

  const extractNum = (s: string | undefined | null): number | null => {
    if (s == null) return null;
    const m = String(s).match(/-?\d+(?:\.\d+)?/);
    return m ? parseFloat(m[0]) : null;
  };

  const filtered = cls === ALL ? compounds : compounds.filter((c) => c.Class === cls);
  const rows = useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.dir === "asc" ? 1 : -1;
    const indexed = filtered.map((c, i) => ({ c, i }));
    indexed.sort((a, b) => {
      let cmp = 0;
      if (sort.key === "Class") {
        cmp = a.c.Class.localeCompare(b.c.Class);
      } else if (
        sort.key === "Analgesic" ||
        sort.key === "Sedative" ||
        sort.key === "Respiratory Depression" ||
        sort.key === "Euphoria" ||
        sort.key === "Dependence Risk"
      ) {
        cmp = (a.c[sort.key] as number) - (b.c[sort.key] as number);
      } else {
        const av =
          sort.key === "Half-Life"
            ? extractNum(a.c.pharma?.["Half-Life"])
            : extractNum((a.c as any)[sort.key]);
        const bv =
          sort.key === "Half-Life"
            ? extractNum(b.c.pharma?.["Half-Life"])
            : extractNum((b.c as any)[sort.key]);
        if (av === null && bv === null) cmp = 0;
        else if (av === null) return 1;
        else if (bv === null) return -1;
        else cmp = av - bv;
      }
      return cmp !== 0 ? cmp * dir : a.i - b.i;
    });
    return indexed.map((x) => x.c);
  }, [filtered, sort]);

  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const SortTH = ({ k, label }: { k: SortKey; label: string }) => {
    const active = sort?.key === k;
    const arrow = active ? (sort!.dir === "asc" ? "▲" : "▼") : "⇅";
    return (
      <th
        style={{ ...TH, cursor: "pointer", userSelect: "none", position: "sticky", top: 0, zIndex: 1 }}
        onClick={() => toggleSort(k)}
      >
        {label}{" "}
        <span style={{ color: active ? "var(--accent)" : "var(--text-muted)", fontSize: "0.75rem" }}>
          {arrow}
        </span>
      </th>
    );
  };

  const dash = (v: string | undefined | null) =>
    v && String(v).trim() !== "" ? (
      v
    ) : (
      <span style={{ color: "var(--text-muted)", opacity: 0.6 }}>-</span>
    );


  return (
    <div data-section="opioide">
      <h1 className="page-title">Opioid-Effekte-Vergleich</h1>
      <div className="page-title-bar" />

      <FentanylWarnings />
      <MethadonWarnings />

      <section
        className="glass-card"
        aria-labelledby="opioid-serotonin-heading"
        style={{ padding: "clamp(0.85rem, 3vw, 1.5rem)", marginBottom: "1.5rem", lineHeight: 1.6 }}
      >
        <h2 id="opioid-serotonin-heading" style={{ marginTop: 0, fontSize: "1.1rem" }}>
          Serotonin-Syndrom: Wechselwirkungen beachten
        </h2>
        <p>
          Einige Opioide wirken zusätzlich auf das Serotoninsystem. Zusammen mit weiteren
          serotonergen Mitteln, etwa SSRI, SNRI und besonders MAO-A-Hemmern, kann das Risiko
          eines seltenen, aber potenziell lebensbedrohlichen Serotonin-Syndroms steigen.
          Auch Dextromethorphan (DXM), Linezolid und MDMA werden in LONTS genannt.
        </p>
        <p>
          Die LONTS-Konsultationsfassung 4.0 nennt eine Hemmung des Serotonin-Transporters
          (SERT) bei Tramadol, Tapentadol und Methadon sowie eine direkte
          Serotoninfreisetzung bei Fentanyl und Tramadol. Daraus folgt kein gleich hohes
          Risiko für alle Opioide oder Kombinationen.
        </p>
        <p>
          Konkrete Präparate vor einer Kombination ärztlich oder in der Apotheke prüfen
          lassen; verordnete Medikamente nicht eigenständig absetzen oder umstellen.
          Bei Verdacht auf ein Serotonin-Syndrom sofort medizinische Hilfe holen.
        </p>
        <p style={{ marginBottom: 0, fontSize: "0.85rem" }}>
          Quelle: <a
            href={SOURCES["awmf-lonts-4-2026-kf"].url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "var(--accent)", textDecoration: "underline" }}
          >
            LONTS 4.0, Konsultationsfassung September 2026, Kapitel 6.2.9,
            Abschnitt „Serotonin-Syndrom“, S. 57–58
          </a>. Noch keine endgültige Fassung; geprüft am 07.10.2026.
        </p>
      </section>




      {classes.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1.25rem" }}>
          {classes.map((c) => {
            const active = c === cls;
            const label = c === ALL ? "Alle" : c;
            return (
              <button
                key={c}
                onClick={() => setCls(c)}
                className={active ? "" : "glass-card"}
                style={{
                  borderRadius: 6,
                  padding: "0.35rem 0.8rem",
                  fontSize: "0.8rem",
                  cursor: "pointer",
                  background: active ? "var(--accent)" : undefined,
                  color: active ? "var(--ink-on-accent)" : "var(--text-muted)",
                  border: active ? "1px solid var(--accent)" : undefined,

                  fontWeight: active ? 600 : 400,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      <div
        className="glass-card"
        style={{ overflowX: "auto", padding: 0, background: "var(--bg-surface)" }}
      >
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 900 }}>
          <thead>
            <tr>
              <th style={{ ...TH, position: "sticky", top: 0, zIndex: 1 }}>Substanz</th>
              <SortTH k="Class" label="Klasse" />
              <SortTH k="Half-Life" label="t½ (h)" />
              <SortTH k="Equiv. to Morphin 10mg" label="Äquiv. zu Morphin 10mg" />
              <SortTH k="Typical Dose (mg)" label="Typische Dosis (mg)" />
              <SortTH k="Analgesic" label="Analgetisch" />
              <SortTH k="Sedative" label="Sedierend" />
              <SortTH k="Respiratory Depression" label="Atemdepression" />
              <SortTH k="Euphoria" label="Euphorie" />
              <SortTH k="Dependence Risk" label="Abhängigkeit" />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const open = expanded === c.Substance;
              const notesText = c.Notes_de && c.Notes_de.trim() !== "" ? c.Notes_de : c.Notes;
              return (
                <Fragment key={c.Substance}>
                  <tr
                    onClick={() => setExpanded(open ? null : c.Substance)}
                    style={{ cursor: "pointer", background: open ? "rgba(255,255,255,0.03)" : undefined }}
                    onMouseEnter={(e) => {
                      if (!open) (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.03)";
                    }}
                    onMouseLeave={(e) => {
                      if (!open) (e.currentTarget as HTMLElement).style.background = "";
                    }}
                  >
                    <td style={{ ...TD, fontWeight: 600 }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                        {c.warning && (
                          <AlertTriangle
                            size={14}
                            style={{ color: "#f87171", flexShrink: 0 }}
                            aria-label="Warnhinweis"
                          />
                        )}
                        {c.Substance}
                      </span>
                    </td>
                    <td style={{ ...TD, color: "var(--text-secondary)" }}>{dash(c.Class)}</td>
                    <td style={TD}>{dash(c.pharma?.["Half-Life"])}</td>
                    <td style={TD}>{dash(c["Equiv. to Morphin 10mg"])}</td>
                    <td style={TD}>{dash(c["Typical Dose (mg)"])}</td>
                    <td style={TD}><DotRating value={c.Analgesic} color="var(--accent-cyan)" /></td>
                    <td style={TD}><DotRating value={c.Sedative} color="var(--accent-purple)" /></td>
                    <td style={TD}><DotRating value={c["Respiratory Depression"]} color="#f87171" /></td>
                    <td style={TD}><DotRating value={c.Euphoria} color="#fb923c" /></td>
                    <td style={TD}><DotRating value={c["Dependence Risk"]} color="#f87171" /></td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={10} style={{ background: "rgba(26,16,51,0.9)", padding: "1rem 0.75rem" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                          <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", lineHeight: 1.5 }}>
                            {notesText && notesText.trim() !== "" ? notesText : "Noch keine Daten hinterlegt."}
                          </p>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem", fontSize: "0.8rem" }}>
                            <div>
                              <span style={{ color: "var(--text-muted)" }}>Max. Tagesdosis: </span>
                              <span style={{ color: "var(--text-primary)" }}>{dash(c["Max Daily (mg)"])}</span>
                            </div>
                          </div>
                          {c.pharma && Object.keys(c.pharma).length > 0 ? (
                            <div style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                              gap: "0.4rem 1.25rem",
                              borderTop: "1px solid var(--border-purple)",
                              paddingTop: "0.75rem",
                            }}>
                              {PHARMA_ORDER.filter((k) => c.pharma?.[k] != null).map((k) => (
                                <div key={k} style={{ fontSize: "0.8rem" }}>
                                  <span style={{ color: "var(--text-muted)" }}>{PHARMA_LABELS[k]}: </span>
                                  <span style={{ color: "var(--text-primary)" }}>{c.pharma?.[k]}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{
                              borderTop: "1px solid var(--border-purple)",
                              paddingTop: "0.75rem",
                              color: "var(--text-muted)",
                              fontSize: "0.8rem",
                              fontStyle: "italic",
                              opacity: 0.7,
                            }}>
                              Keine pharmakologischen Daten hinterlegt.
                            </div>
                          )}

                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {rows.some((c) => c.nonComparable) && (
        <p style={{ marginTop: "1.25rem", color: "var(--text-muted)", fontSize: "0.8rem", lineHeight: 1.55 }}>
          <span style={{ color: "#fca5a5" }}>*</span> Der Methadon-Morphin-Umrechnungsfaktor
          ist nicht-linear (steigt mit der Tagesdosis). Deshalb steht hier bewusst "variabel"
          statt einer festen Zahl, siehe Warnhinweis oben.
        </p>
      )}

      <p style={{ marginTop: "1.5rem", color: "var(--text-muted)", fontSize: "0.8rem", lineHeight: 1.5 }}>
        Diese Tabelle dient Informationszwecken. Kein Ersatz für medizinische Beratung.
      </p>
      <PageSources path="/opioide" />
    </div>
  );
}
